import React from 'react';
import {
  FaBookOpen,
  FaBullseye,
  FaCompass,
  FaInfoCircle,
  FaMapMarkedAlt,
  FaMedal,
  FaRoute,
  FaShieldAlt,
  FaStar,
} from 'react-icons/fa';
import { XP_EVENTS, useXpBonus } from '../hooks/useXpBonus';

const formatXp = (value) => {
  const number = Number(value || 0);
  return `${number.toLocaleString('de-DE')} XP`;
};

const levelExamples = [
  { xp: 0, level: 0, rank: 'Matrose' },
  { xp: 100, level: 1, rank: 'Matrose' },
  { xp: 400, level: 2, rank: 'Matrose' },
  { xp: 900, level: 3, rank: 'Deckhelfer' },
  { xp: 2500, level: 5, rank: 'Steuermann' },
  { xp: 6400, level: 8, rank: 'Kapitän' },
  { xp: 14400, level: 12, rank: 'Admiral' },
];

const helpTopics = [
  {
    id: 'schnellstart',
    icon: <FaBullseye aria-hidden="true" />,
    title: 'Schnellstart',
    summary: 'Die ersten sinnvollen Schritte: Karte öffnen, Pfand melden, XP sammeln und Profil prüfen.',
  },
  {
    id: 'xp',
    icon: <FaMedal aria-hidden="true" />,
    title: 'XP & Level',
    summary: 'Alle wichtigen XP-Quellen, Level-Grenzen, Ränge und Freischaltungen an einem Ort.',
  },
  {
    id: 'karte',
    icon: <FaMapMarkedAlt aria-hidden="true" />,
    title: 'Schatzkarte',
    summary: 'Pfandfunde, Abholungen, Dinge zum Verschenken und eigene Orte auf der Karte verstehen.',
  },
  {
    id: 'flaschenpost',
    icon: <FaRoute aria-hidden="true" />,
    title: 'Flaschenpost',
    summary: 'Temporäre Schatzorte mit Nachricht platzieren und kleine eigene Routen bauen.',
  },
  {
    id: 'expeditionen',
    icon: <FaCompass aria-hidden="true" />,
    title: 'Expeditionen',
    summary: 'Scans, Kartenfragmente, neue Gebiete, Pfand-Beifang und digitale Funde.',
  },
  {
    id: 'fairness',
    icon: <FaShieldAlt aria-hidden="true" />,
    title: 'Fair spielen',
    summary: 'Warum es Cooldowns, Limits und Level-Freischaltungen gibt.',
  },
];

const xpGroups = [
  {
    title: 'Pfand sammeln und melden',
    description: 'Diese Aktionen hängen direkt mit echten Pfandeinträgen, Fundorten und Abholungen zusammen.',
    rows: [
      { label: 'Pfandeintrag hinzufügen', value: XP_EVENTS.ADD_PFAND, detail: 'pro gezählter Einheit' },
      { label: 'Manueller Produkttreffer / Freitext', value: XP_EVENTS.FREITEXT, detail: 'pro gezählter Einheit' },
      { label: 'Barcode scannen', value: XP_EVENTS.BARCODE_SCAN, detail: 'pro gezählter Einheit' },
      { label: 'Pfandfund oder "zu verschenken" markieren', value: XP_EVENTS.MARK_PFANDSPENDE },
      { label: 'Gemeldete Pfandspende abholen', value: XP_EVENTS.PICKUP_PFANDSPENDE },
      { label: 'Deine Meldung hat geholfen', value: XP_EVENTS.PFANDSPENDE_CONFIRMED },
      { label: 'Pfand einlösen', value: XP_EVENTS.REDEEM_PFAND },
      { label: 'Pfandeintrag wieder löschen', value: XP_EVENTS.REMOVE_ENTRY },
    ],
  },
  {
    title: 'Tagesaufgabe und Bewegung',
    description: 'Pfandpirat belohnt nicht nur Einträge, sondern auch regelmäßige Aktivität draußen.',
    rows: [
      { label: 'Tagesaufgabe erfüllen', value: XP_EVENTS.COMPLETE_DAILY, detail: 'einmal pro Tag' },
      { label: 'tägliches Schrittziel erreichen', value: XP_EVENTS.STEPS_GOAL },
      { label: '3-Tage-Schritte-Streak', value: XP_EVENTS.STEPS_STREAK_3 },
      { label: '7-Tage-Schritte-Streak', value: XP_EVENTS.STEPS_STREAK_7 },
      { label: '30-Tage-Schritte-Streak', value: XP_EVENTS.STEPS_STREAK_30 },
      { label: '10.000 Gesamtschritte', value: XP_EVENTS.STEPS_TOTAL_10000 },
      { label: '50.000 Gesamtschritte', value: XP_EVENTS.STEPS_TOTAL_50000 },
      { label: '100.000 Gesamtschritte', value: XP_EVENTS.STEPS_TOTAL_100000 },
    ],
  },
  {
    title: 'Expeditionen',
    description: 'Der Scanner macht echte Bewegung und digitale Funde zu einer kleinen Schatzsuche.',
    rows: [
      { label: 'Expeditions-Scan', value: XP_EVENTS.EXPEDITION_SCAN },
      { label: 'neues Gebiet entdecken', fixed: 10, detail: 'zusätzlich' },
      { label: 'Schatzkarte vervollständigen', value: XP_EVENTS.EXPEDITION_MAP_COMPLETE },
      { label: 'Pfand-Beifang nach echtem Pfandeintrag', value: XP_EVENTS.EXPEDITION_PFAND_BONUS },
    ],
  },
  {
    title: 'Loot, Inventar und Community',
    description: 'Diese Belohnungen entstehen durch Spielaktionen, Items und Beiträge zur Datenbasis.',
    rows: [
      { label: 'Flaschenpost zum ersten Mal öffnen', value: XP_EVENTS.OPEN_BOTTLE_MESSAGE },
      { label: 'Loot finden', value: XP_EVENTS.LOOT_FIND },
      { label: 'XP-Boost benutzen', value: XP_EVENTS.USE_XP_BOOST },
      { label: 'Freund erfolgreich eingeladen', value: XP_EVENTS.REFERRAL_QUALIFIED },
      { label: 'freigegebener Getränkevorschlag', value: XP_EVENTS.DRINK_SUBMISSION_APPROVED },
      { label: 'Tavernenrunde abschließen', value: XP_EVENTS.TAVERN_GAME_COMPLETE, detail: 'Basiswert, real abhängig vom Ergebnis' },
    ],
  },
];

function HelpTopicCard({ topic }) {
  const handleClick = () => {
    const target = document.getElementById(topic.id);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <button type="button" className="pfp-help-topic-card" onClick={handleClick}>
      <span className="pfp-help-topic-card__icon">{topic.icon}</span>
      <span>
        <strong>{topic.title}</strong>
        <em>{topic.summary}</em>
      </span>
    </button>
  );
}

function XpRow({ row, xpConfig }) {
  const value = row.fixed !== undefined ? row.fixed : xpConfig[row.value];
  return (
    <tr>
      <td>{row.label}</td>
      <td>{formatXp(value)}</td>
      <td>{row.detail || 'Standardbelohnung'}</td>
    </tr>
  );
}

function XpGroup({ group, xpConfig }) {
  return (
    <section className="pfp-help-xp-group">
      <div className="pfp-help-xp-group__head">
        <h3>{group.title}</h3>
        <p>{group.description}</p>
      </div>
      <div className="pfp-help-table-wrap">
        <table className="pfp-help-table">
          <thead>
            <tr>
              <th>Aktion</th>
              <th>XP</th>
              <th>Hinweis</th>
            </tr>
          </thead>
          <tbody>
            {group.rows.map((row) => (
              <XpRow key={`${group.title}-${row.label}`} row={row} xpConfig={xpConfig} />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function HelpCenterPage() {
  const { xpConfig } = useXpBonus();

  return (
    <main className="pfp-pirate-page pfp-help-page">
      <header className="pfp-pirate-hero pfp-help-hero">
        <span className="pfp-pirate-kicker">Hilfe & Spielregeln</span>
        <h1>Pfandpirat verstehen</h1>
        <p>
          Hier findest du die wichtigsten Regeln, Belohnungen und Funktionen der App gesammelt an einem Ort.
        </p>
      </header>

      <section className="pfp-help-grid" aria-label="Hilfethemen">
        {helpTopics.map((topic) => (
          <HelpTopicCard key={topic.id} topic={topic} />
        ))}
      </section>

      <section id="schnellstart" className="pfp-help-section">
        <div className="pfp-help-section__icon"><FaBookOpen aria-hidden="true" /></div>
        <div>
          <h2>Schnellstart</h2>
          <p>
            Wenn du neu bist, starte mit der Karte oder der Suche. Du kannst Pfand selbst eintragen,
            Pfandfundorte für andere markieren oder gemeldete Fundorte abholen. Mit Account sammelst du
            dabei XP, Level, Achievements und Inventar-Gegenstände.
          </p>
          <div className="pfp-help-action-row">
            <a className="pfand-button pfand-button--secondary" href="#/map">Karte öffnen</a>
            <a className="pfand-button" href="#/quick-mark">Pfand markieren</a>
            <a className="pfand-button" href="#/search">Getränk suchen</a>
          </div>
        </div>
      </section>

      <section id="xp" className="pfp-help-section pfp-help-section--wide">
        <div className="pfp-help-section__icon"><FaStar aria-hidden="true" /></div>
        <div>
          <h2>Wofür bekomme ich XP?</h2>
          <p>
            XP sind deine Erfahrungspunkte. Du bekommst sie, wenn du Pfand sichtbar machst,
            Fundorte nutzt, Expeditionen spielst, Aufgaben abschließt oder der Community hilfst.
            Die Werte kommen aus der aktuellen App-Konfiguration und können sich mit Balancing-Updates ändern.
          </p>
        </div>
      </section>

      <div className="pfp-help-xp-layout">
        {xpGroups.map((group) => (
          <XpGroup key={group.title} group={group} xpConfig={xpConfig} />
        ))}
      </div>

      <section className="pfp-help-section pfp-help-section--wide">
        <div className="pfp-help-section__icon"><FaMedal aria-hidden="true" /></div>
        <div>
          <h2>Level und Ränge</h2>
          <p>
            Dein Level steigt mit deiner gesamten XP-Zahl. Je höher du kommst, desto mehr XP brauchst du
            für den nächsten Schritt. Ab Level 5 kannst du eigene dauerhafte Orte auf der Karte anlegen.
          </p>
          <div className="pfp-help-table-wrap">
            <table className="pfp-help-table">
              <thead>
                <tr>
                  <th>XP gesamt</th>
                  <th>Level</th>
                  <th>Rang</th>
                </tr>
              </thead>
              <tbody>
                {levelExamples.map((item) => (
                  <tr key={item.xp}>
                    <td>{item.xp.toLocaleString('de-DE')} XP</td>
                    <td>Level {item.level}</td>
                    <td>{item.rank}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section id="karte" className="pfp-help-section">
        <div className="pfp-help-section__icon"><FaMapMarkedAlt aria-hidden="true" /></div>
        <div>
          <h2>Schatzkarte und Fundorte</h2>
          <p>
            Auf der Karte siehst du Pfandfunde, Dinge zum Verschenken, Flaschenposten, eigene Orte
            und weitere spielbare Punkte. Wenn du etwas meldest, hilfst du anderen Menschen, Pfand oder
            brauchbare Gegenstände schneller zu finden.
          </p>
          <p>
            Ohne Account kannst du bereits Pfand und Dinge zum Verschenken markieren. Mit Account kommen
            XP, Level, Achievements, Rückmeldungen und dein persönlicher Fortschritt dazu.
          </p>
        </div>
      </section>

      <section id="flaschenpost" className="pfp-help-section">
        <div className="pfp-help-section__icon"><FaRoute aria-hidden="true" /></div>
        <div>
          <h2>Flaschenpost und eigene Schatzrouten</h2>
          <p>
            Neue Accounts erhalten drei Flaschenpost-Items. Mit einer Flaschenpost kannst du einen
            temporären Ort auf der Karte platzieren und eine kurze Nachricht hinterlassen. Andere Nutzer
            können dorthin navigieren, die Nachricht lesen und vor Ort looten.
          </p>
          <p>
            Flaschenpost-Orte bleiben maximal 30 Tage sichtbar. Beim ersten Öffnen gibt es XP, beim Looten
            können zusätzlich Münzen oder Items entstehen. Damit daraus kein Dauerklicken wird, gelten
            Cooldowns und Fairness-Regeln.
          </p>
        </div>
      </section>

      <section className="pfp-help-section">
        <div className="pfp-help-section__icon"><FaMapMarkedAlt aria-hidden="true" /></div>
        <div>
          <h2>Eigene Orte ab Level 5</h2>
          <p>
            Ab Level 5 kannst du dauerhafte eigene Orte in der Karte anlegen, zum Beispiel Parkbänke,
            Mülleimer, Bushaltestellen oder markante Treffpunkte. Ein Foto und eine Beschreibung helfen
            anderen, den Ort besser zu erkennen.
          </p>
          <p>
            Level 5 ist bewusst eine Vertrauensschwelle: Wer eigene dauerhafte Orte setzen kann, soll
            vorher schon gezeigt haben, dass er oder sie die App fair nutzt.
          </p>
        </div>
      </section>

      <section id="expeditionen" className="pfp-help-section">
        <div className="pfp-help-section__icon"><FaCompass aria-hidden="true" /></div>
        <div>
          <h2>Expeditionen</h2>
          <p>
            In Expeditionen nutzt du Scans, Energie und Bewegung, um digitale Funde zu entdecken.
            Neue Gebiete, Kartenfragmente und vollständige Schatzkarten bringen zusätzliche Belohnungen.
            Nach echten Pfandeinträgen kann außerdem ein digitaler Pfand-Beifang ausgelöst werden.
          </p>
          <div className="pfp-help-action-row">
            <a className="pfand-button pfand-button--secondary" href="#/expeditions">Expedition starten</a>
            <a className="pfand-button" href="#/inventar">Inventar ansehen</a>
          </div>
        </div>
      </section>

      <section className="pfp-help-section">
        <div className="pfp-help-section__icon"><FaInfoCircle aria-hidden="true" /></div>
        <div>
          <h2>Inventar, Hafen, Taverne und Werft</h2>
          <p>
            Im Inventar sammelst du Münzen, Items und besondere Gegenstände wie XP-Boosts oder
            Flaschenpost. Im Freibeuterhafen warten Aufträge, in der Taverne Minispiele und in der
            Werft beziehungsweise Fundwerkstatt kannst du Materialien und Rezepte nutzen.
          </p>
          <div className="pfp-help-action-row">
            <a className="pfand-button" href="#/freibeuterhafen">Freibeuterhafen</a>
            <a className="pfand-button" href="#/freibeuterhafen/taverne">Taverne</a>
            <a className="pfand-button" href="#/freibeuterhafen/werft">Werft</a>
          </div>
        </div>
      </section>

      <section id="fairness" className="pfp-help-section">
        <div className="pfp-help-section__icon"><FaShieldAlt aria-hidden="true" /></div>
        <div>
          <h2>Fairness, Limits und Datenschutz</h2>
          <p>
            Pfandpirat soll Spaß machen, aber fair bleiben. Deshalb geben doppelte Aktionen nicht
            unbegrenzt XP, manche Belohnungen sind einmalig oder täglich begrenzt und bestimmte
            Funktionen werden erst nach etwas Spielfortschritt freigeschaltet.
          </p>
          <p>
            Markiere keine privaten Orte, keine fremden Personen und keine sensiblen Situationen.
            Die Karte soll helfen, Pfand und brauchbare Gegenstände sichtbarer zu machen, ohne Menschen
            bloßzustellen.
          </p>
        </div>
      </section>

    </main>
  );
}
